import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NomenclatorEntry,
  NomenclatorImportMode,
  NomenclatorImportRow,
  NomenclatorImportSummary,
} from '@lyra/shared';
import { apiFetch } from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { notifyError, notifySuccess } from '@/lib/useMutationToast';

/**
 * Nomenclator catalog CRUD hooks (Plan 3d Task 3). Server side shipped in
 * Task 2 (server/src/routes/nomenclator.ts) — org-scoped, writes gated by
 * `requireNomenclatorEdit` (403 for a member without `nomenclator.edit`).
 * No server changes here.
 */

export interface NomenclatorListParams {
  search?: string;
  codBdMedoc?: string;
  codMedoc?: string;
  limit: number;
  offset: number;
}

/**
 * `GET /nomenclator?search=&codBdMedoc=&codMedoc=&limit=&offset=` — the
 * catalog browser's paginated list. Response shape verified against
 * `data.listNomenclator` (server/src/db/data.ts ~L750-794): `{ items, total }`,
 * each item `{ id, codBdMedoc, codMedoc, denumireMedoc, createdAt }` — matches
 * `NomenclatorEntry` from `@lyra/shared` exactly.
 *
 * Uses the NEW `queryKeys.nomenclatorList` key (distinct from the existing
 * `queryKeys.nomenclator(term)` used by /mapari's correction search) so this
 * list and that search cache independently.
 */
export function useNomenclator(params: NomenclatorListParams) {
  const qs = new URLSearchParams();
  if (params.search && params.search.trim()) qs.set('search', params.search.trim());
  if (params.codBdMedoc && params.codBdMedoc.trim()) qs.set('codBdMedoc', params.codBdMedoc.trim());
  if (params.codMedoc && params.codMedoc.trim()) qs.set('codMedoc', params.codMedoc.trim());
  qs.set('limit', String(params.limit));
  qs.set('offset', String(params.offset));

  return useQuery({
    queryKey: queryKeys.nomenclatorList(params),
    queryFn: () => apiFetch<{ items: NomenclatorEntry[]; total: number }>(`/nomenclator?${qs.toString()}`),
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetches the ENTIRE filtered catalog for export — not just the visible page.
 * The server caps `limit` at 200, so this pages through in 200-row chunks until
 * it has all `total` matching rows (respecting the current search / code filters).
 * Exposed as a mutation for the isPending / onError states the export button needs.
 */
export function useExportNomenclator() {
  return useMutation({
    mutationFn: async (filters: { search?: string; codBdMedoc?: string; codMedoc?: string }) => {
      const PAGE = 200;
      const all: NomenclatorEntry[] = [];
      let offset = 0;
      let total = Infinity;
      while (offset < total) {
        const qs = new URLSearchParams();
        if (filters.search?.trim()) qs.set('search', filters.search.trim());
        if (filters.codBdMedoc?.trim()) qs.set('codBdMedoc', filters.codBdMedoc.trim());
        if (filters.codMedoc?.trim()) qs.set('codMedoc', filters.codMedoc.trim());
        qs.set('limit', String(PAGE));
        qs.set('offset', String(offset));
        const res = await apiFetch<{ items: NomenclatorEntry[]; total: number }>(`/nomenclator?${qs.toString()}`);
        total = res.total;
        all.push(...res.items);
        if (res.items.length === 0) break; // safety: never loop on an empty page
        offset += PAGE;
      }
      return all;
    },
    onError: (error) => notifyError(error, 'Exportul a eșuat.'),
  });
}

/** Invalidates the `['nomenclator']` prefix — covers both this list's cache
 *  (`['nomenclator','list',params]`) AND /mapari's search cache
 *  (`['nomenclator', term]`), since both share the same first key segment. */
function useInvalidateNomenclator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['nomenclator'] });
}

/**
 * `POST /nomenclator` — verified against `data.createNomenclatorEntry`
 * (server/src/db/data.ts ~L796-808): 201, body `{codBdMedoc,codMedoc,
 * denumireMedoc}` → returns `{id,codBdMedoc,codMedoc,denumireMedoc}` (no
 * `createdAt` — the INSERT's RETURNING clause doesn't select it; harmless
 * since `NomenclatorEntry.createdAt` is optional).
 */
export function useCreateNomenclator() {
  const invalidate = useInvalidateNomenclator();
  return useMutation({
    mutationFn: (body: { codBdMedoc: string; codMedoc: string; denumireMedoc: string }) =>
      apiFetch<NomenclatorEntry>('/nomenclator', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Înregistrarea a fost salvată.');
    },
    onError: (error) => notifyError(error, 'Salvarea înregistrării a eșuat.'),
  });
}

export interface UpdateNomenclatorVars {
  id: number;
  codBdMedoc?: string;
  codMedoc?: string;
  denumireMedoc?: string;
}

/**
 * `PATCH /nomenclator/:id` — verified against `data.updateNomenclatorEntry`
 * (server/src/db/data.ts ~L827-870): 200 with the updated entry, or throws
 * `NomenclatorDuplicateError` (statusCode 409) on a UNIQUE-constraint
 * collision, which `app.ts`'s global error handler turns into a plain
 * `{ error }` 409 — `apiFetch` surfaces that as `ApiClientError(409, msg)`.
 * That error is left to propagate (not caught here) so the page can show
 * the server's Romanian duplicate message.
 */
export function useUpdateNomenclator() {
  const invalidate = useInvalidateNomenclator();
  return useMutation({
    mutationFn: ({ id, ...fields }: UpdateNomenclatorVars) =>
      apiFetch<NomenclatorEntry>(`/nomenclator/${id}`, { method: 'PATCH', body: JSON.stringify(fields) }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Înregistrarea a fost salvată.');
    },
    onError: (error) => notifyError(error, 'Salvarea înregistrării a eșuat.'),
  });
}

/** `DELETE /nomenclator/:id` — verified against `data.deleteNomenclatorEntry`
 *  (server/src/db/data.ts ~L872-895): 204 no body on success (`apiFetch`
 *  returns `undefined` for 204/205), 404 if the id doesn't exist in this org. */
export function useDeleteNomenclator() {
  const invalidate = useInvalidateNomenclator();
  return useMutation({
    mutationFn: (id: number) => apiFetch<void>(`/nomenclator/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Înregistrarea a fost ștearsă.');
    },
    onError: (error) => notifyError(error, 'Ștergerea înregistrării a eșuat.'),
  });
}

/** `POST /api/nomenclator/import` — bulk import (Excel/CSV → column mapping).
 *  `mode: 'update'` upserts by COD MEDOC; `mode: 'replace'` wipes the org's
 *  entries first, then loads the file. Returns `{ created, updated, deleted }`.
 *  Writes are gated by `requireNomenclatorEdit` (a non-admin gets 403 →
 *  `ApiClientError`). Invalidates the list on success so the table refreshes.
 *
 *  Deliberately NOT toasted (2026-08-19 remediation Slice C, Task 2):
 *  `NomenclatorImport.tsx` already renders the returned `{created,updated,
 *  deleted}` as a persistent, detailed results screen — a toast on top
 *  would be redundant with a summary the operator is already looking at. */
export function useImportNomenclator() {
  const invalidate = useInvalidateNomenclator();
  return useMutation({
    mutationFn: (payload: { rows: NomenclatorImportRow[]; mode: NomenclatorImportMode }) =>
      apiFetch<NomenclatorImportSummary>('/api/nomenclator/import', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: invalidate,
  });
}
